import { Component, Input, OnInit, inject } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import {
    Currency,
    Locale,
    WdxDestroyClass,
    customCurrencyMaskConfig,
} from '@wdx/shared/utils';
import { NgxCurrencyConfig } from 'ngx-currency';
import { combineLatest, Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { IFormDynamicData } from '@wdx/shared/infrastructure/form-framework';

@Component({
    selector: 'clmi-workflow-designer-currency-control',
    templateUrl: './workflow-designer-currency-control.component.html',
    standalone: false,
})
export class WorkflowDesignerCurrencyControlComponent
    extends WdxDestroyClass
    implements OnInit
{
    private readonly dynamicDataService = inject(IFormDynamicData);

    @Input() formGroup?: FormGroup;
    @Input() controlName?: string;
    @Input() isRequired?: boolean;

    public currencyFormGroup = new FormGroup({
        amount: new FormControl<number | null>(null),
        isoCode: new FormControl<Currency | null>(null),
    });
    public currencyMaskConfig: Partial<NgxCurrencyConfig> = {
        ...customCurrencyMaskConfig,
        allowNegative: true,
    };
    public currencySymbol = '';
    public currencyPlaceholder = '';
    public currencies$?: Observable<Currency[]>;
    public locale$?: Observable<Locale>;

    get amountControl(): FormControl<number | null> {
        return this.currencyFormGroup.get('amount') as FormControl;
    }

    get isoCodeControl(): FormControl<Currency | null> {
        return this.currencyFormGroup.get('isoCode') as FormControl;
    }

    ngOnInit(): void {
        if (!this.formGroup || !this.controlName) {
            return;
        }

        const control = this.formGroup.get(this.controlName) as FormControl;
        const existing = control?.value;
        const parsedExisting = this.parseCurrencyValue(existing);
        const existingAmount = parsedExisting?.amount ?? null;
        const existingIsoCode = parsedExisting?.isoCode ?? null;

        this.locale$ = this.dynamicDataService.getMeLocale();
        this.currencies$ = this.dynamicDataService.getCurrencies();

        combineLatest([this.locale$, this.currencies$])
            .pipe(takeUntil(this.destroyed$))
            .subscribe(([locale, currencies]) => {
                this.currencyMaskConfig = {
                    ...this.currencyMaskConfig,
                    decimal: locale?.numberFormat
                        ?.numberDecimalSeparator as string,
                    thousands: locale?.numberFormat
                        ?.numberGroupSeparator as string,
                };

                this.currencyPlaceholder = `${Number(0.0).toLocaleString(
                    locale?.code as string,
                    {
                        minimumFractionDigits: 2,
                    },
                )}`;

                const defaultIsoCode =
                    existingIsoCode || locale.currency?.isoCode;
                const defaultCurrency = currencies.find(
                    (currency) => currency.isoCode === defaultIsoCode,
                );
                const fallback = currencies[0];
                const selected = defaultCurrency ?? fallback ?? null;

                if (selected) {
                    this.currencySymbol = selected.symbol || selected.isoCode;
                    this.isoCodeControl.setValue(selected, {
                        emitEvent: false,
                    });
                }

                if (existingAmount !== null && existingAmount !== undefined) {
                    this.amountControl.setValue(existingAmount, {
                        emitEvent: false,
                    });
                }
            });

        this.amountControl.valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe((amount) => {
                this.setCurrencyValue(
                    amount,
                    this.isoCodeControl.value?.isoCode,
                    control,
                );
            });

        this.isoCodeControl.valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe((currency) => {
                this.currencySymbol =
                    currency?.symbol || currency?.isoCode || '';
                this.setCurrencyValue(
                    this.amountControl.value,
                    currency?.isoCode,
                    control,
                );
            });

        control?.valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe((value: unknown) => {
                const parsed = this.parseCurrencyValue(value);
                if (!parsed) {
                    return;
                }

                if (parsed.amount !== this.amountControl.value) {
                    this.amountControl.setValue(parsed.amount, {
                        emitEvent: false,
                    });
                }

                if (
                    parsed.isoCode &&
                    parsed.isoCode !== this.isoCodeControl.value?.isoCode
                ) {
                    this.currencySymbol = parsed.isoCode;
                }
            });
    }

    public compareSelectedCurrency(a: Currency, b: Currency) {
        return a?.isoCode === b?.isoCode;
    }

    private setCurrencyValue(
        amount: number | null,
        isoCode: string | undefined,
        control: FormControl,
    ) {
        if (typeof amount !== 'number' || !isoCode) {
            control.setValue(null);
            return;
        }

        control.setValue(`${isoCode}:${amount}`);
    }

    private parseCurrencyValue(value: unknown): {
        amount: number;
        isoCode: string;
    } | null {
        if (!value) {
            return null;
        }

        let parsed: { isoCode: string; amount: number } | null = null;

        if (typeof value === 'string') {
            const [isoCode, amountText] = value.split(':');
            parsed = { isoCode, amount: Number(amountText) };
        } else if (
            value !== null &&
            typeof value === 'object' &&
            (value as { amount?: unknown }).amount !== undefined
        ) {
            const currencyValue = value as {
                amount?: unknown;
                isoCode?: unknown;
            };
            parsed = {
                isoCode: String(currencyValue.isoCode ?? ''),
                amount: Number(currencyValue.amount),
            };
        }

        return parsed && parsed.isoCode && !Number.isNaN(parsed.amount)
            ? parsed
            : null;
    }
}
